/**
 * Execution Monitor - real-time pipeline execution tracking (graph + list views)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import pipelineApi, { isDataSourceNode, sanitizeStepName } from '../api/client';
import { PipelineCanvas } from './PipelineCanvas';
import { NodeExecutionDetail, useStepInstanceDetail } from './NodeExecutionDetail';
import { JsonViewer } from './JsonViewer';
import type { PipelineDefinition, PipelineExecution, StepInstanceExecution } from '../types/pipeline';
import { copyToClipboard } from '../utils/clipboard';
import { CheckIcon, CircleIcon, CloseIcon, CopyIcon, RefreshIcon } from './icons';
import './ExecutionMonitor.css';

// Data source nodes (task_type in DATA_SOURCE_INPUT_TYPES) never appear in
// WorkflowDefinition.steps, so they have no backend-reported status - derive one
// from whether the execution's resolved scope ids are well-formed UUIDs instead.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// List-tab output section: the step's result is only fetched once the section is expanded.
const NodeOutputSection: React.FC<{ executionId: string; instance: StepInstanceExecution }> = ({
  executionId,
  instance,
}) => {
  const [open, setOpen] = useState(false);
  const { detail, loading, error } = useStepInstanceDetail(executionId, instance, open);
  const result = detail?.result;

  return (
    <div className="node-execution-data">
      <details className="data-section" onToggle={(e) => setOpen(e.currentTarget.open)}>
        <summary className="data-section-header">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 6l4 4 4-4H4z" />
          </svg>
          <span>Output Data</span>
          {result && <span className="data-size">{JSON.stringify(result).length} bytes</span>}
        </summary>
        <div className="data-content">
          {loading ? (
            <span>Loading...</span>
          ) : error ? (
            <span>Failed to load output: {error}</span>
          ) : result ? (
            <>
              <JsonViewer data={result} />
              <button
                className="copy-button"
                onClick={() => copyToClipboard(JSON.stringify(result, null, 2))}
                title="Copy to clipboard"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="3" y="3" width="9" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none" />
                  <path d="M5 3V2a1 1 0 011-1h8a1 1 0 011 1v8a1 1 0 01-1 1h-1" stroke="currentColor" strokeWidth="1.5" fill="none" />
                </svg>
              </button>
            </>
          ) : (
            <span>No output data.</span>
          )}
        </div>
      </details>

      {result?.loop_results && (
        <details className="data-section">
          <summary className="data-section-header">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M4 6l4 4 4-4H4z" />
            </svg>
            <span>Loop Iterations ({result.item_count})</span>
          </summary>
          <div className="data-content">
            {result.loop_results.map((item: any, idx: number) => (
              <details key={idx} className="loop-iteration">
                <summary>Iteration {idx + 1}</summary>
                <JsonViewer data={item} />
              </details>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

interface ExecutionMonitorProps {
  executionId: string | null;
  onClose?: () => void;
  onOpenPipeline?: (pipelineId: string) => void;
}

export const ExecutionMonitor: React.FC<ExecutionMonitorProps> = ({ executionId, onClose, onOpenPipeline }) => {
  const [execution, setExecution] = useState<PipelineExecution | null>(null);
  const [pipeline, setPipeline] = useState<PipelineDefinition | null>(null);
  const [view, setView] = useState<'graph' | 'list'>('graph');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [detailWidth, setDetailWidth] = useState(400);

  // Drag-to-resize the detail panel: dragging left grows it (resizer sits on its
  // left edge), clamped so the canvas always keeps a usable minimum width.
  const handleDetailResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = detailWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      setDetailWidth(Math.min(Math.max(startWidth + delta, 280), 900));
    };

    const handleMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [detailWidth]);

  // Poll the status endpoint (statuses only, no payloads) every 5s; stop once the execution
  // reaches a terminal state. Step prompts/inputs/results can be huge, so they're fetched
  // per instance on demand (NodeExecutionDetail / NodeOutputSection) instead.
  useEffect(() => {
    if (!executionId) {
      setExecution(null);
      return;
    }

    let cancelled = false;
    let interval: ReturnType<typeof setInterval>;

    const poll = () => {
      pipelineApi
        .getExecutionStatus(executionId, false)
        .then((exec) => {
          if (cancelled) return;
          setExecution(exec);
          if (exec.status === 'completed' || exec.status === 'failed') {
            clearInterval(interval);
          }
        })
        .catch(console.error);
    };

    poll();
    interval = setInterval(poll, 5000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [executionId]);

  // Load the full pipeline definition once the execution tells us which workflow
  // it belongs to - the Graph tab needs actual nodes/edges to render onto.
  useEffect(() => {
    if (execution?.pipeline_id) {
      pipelineApi
        .getPipeline(execution.pipeline_id)
        .then(setPipeline)
        .catch(console.error);
    }
  }, [execution?.pipeline_id]);

  // node_executions is keyed by backend step_name (sanitizeStepName(node.id)), not
  // the raw pipeline node id - reconcile both directions here so PipelineCanvas and
  // NodeExecutionDetail can stay agnostic of that backend naming convention.
  const nodeStatuses = useMemo(() => {
    if (!pipeline || !execution) return undefined;
    const map: Record<string, 'pending' | 'running' | 'completed' | 'failed'> = {};
    pipeline.nodes.forEach(node => {
      if (isDataSourceNode(node)) {
        const ids = execution.item_ids;
        map[node.id] = ids && ids.length > 0 && ids.every(id => UUID_PATTERN.test(id)) ? 'completed' : 'failed';
        return;
      }
      const nodeExec = execution.node_executions[sanitizeStepName(node.id)];
      if (nodeExec) {
        map[node.id] = nodeExec.status as 'pending' | 'running' | 'completed' | 'failed';
      }
    });
    return map;
  }, [pipeline, execution]);

  const nodeLabelsByStepName = useMemo(() => {
    const labels: Record<string, string> = {};
    pipeline?.nodes.forEach(node => {
      labels[sanitizeStepName(node.id)] = node.label;
    });
    return labels;
  }, [pipeline]);

  const selectedStepName = selectedNodeId ? sanitizeStepName(selectedNodeId) : null;
  const selectedNodeLabel = pipeline?.nodes.find(n => n.id === selectedNodeId)?.label;

  if (!executionId) {
    return (
      <div className="execution-monitor-empty">
        <svg width="64" height="64" viewBox="0 0 64 64" fill="currentColor" opacity="0.3">
          <circle cx="32" cy="32" r="24" stroke="currentColor" strokeWidth="4" fill="none" />
          <path d="M24 24l16 8-16 8V24z" fill="currentColor" />
        </svg>
        <h3>No Execution Selected</h3>
        <p>Execute a pipeline to see real-time monitoring</p>
      </div>
    );
  }

  if (!execution) {
    return (
      <div className="execution-monitor">
        <div className="execution-monitor-header">
          <h3>Loading Execution...</h3>
        </div>
      </div>
    );
  }

  const completedNodes = Object.values(execution.node_executions).filter(
    n => n.status === 'completed'
  ).length;
  const totalNodes = Object.keys(execution.node_executions).length;
  const completionPercent = totalNodes > 0 ? (completedNodes / totalNodes) * 100 : 0;
  const runningNodeLabels = Object.entries(execution.node_executions)
    .filter(([, n]) => n.status === 'running')
    .map(([stepName]) => nodeLabelsByStepName[stepName] || stepName);

  return (
    <div className="execution-monitor">
      <div className="execution-monitor-header">
        <div className="execution-info">
          <h3>Execution Monitor</h3>
          <span className="connection-status status-connected"><span className="live-dot" /> Auto-refreshing</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="button-secondary"
            onClick={() => {
              if (executionId) {
                pipelineApi
                  .getExecutionStatus(executionId, false)
                  .then(setExecution)
                  .catch(console.error);
              }
            }}
            title="Refresh execution status"
          >
            <RefreshIcon /> Refresh
          </button>
          {onClose && (
            <button className="button-secondary" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>

      <div className="execution-summary">
        <div className="execution-id">
          <span className="summary-label">Execution</span>
          <code>{executionId.slice(0, 8)}</code>
        </div>

        {pipeline?.metadata.name && (
          <div className="execution-pipeline">
            <span className="summary-label">Workflow</span>
            {onOpenPipeline ? (
              <button
                className="pipeline-link"
                onClick={() => onOpenPipeline(execution.pipeline_id)}
                title="Open pipeline in editor"
              >
                {pipeline.metadata.name}
              </button>
            ) : (
              <span>{pipeline.metadata.name}</span>
            )}
          </div>
        )}

        <div className="execution-status">
          <span className="summary-label">Status</span>
          <span className={`status-pill status-${execution.status}`}>
            <span className="status-dot" />
            {execution.status.toLowerCase()}
          </span>
        </div>

        <div className="execution-progress">
          <div className="progress-info">
            <span className="summary-label">Progress</span>
            <span className="progress-percent">{completionPercent.toFixed(0)}%</span>
          </div>
          <div className="progress-bar">
            <div
              className={`progress-fill status-${execution.status}`}
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          <div className="progress-stats">
            {completedNodes} of {totalNodes} nodes completed
          </div>
          {runningNodeLabels.length > 0 && (
            <div className="progress-running-now">
              <span className="running-dot" />
              Running: {runningNodeLabels.join(', ')}
            </div>
          )}
        </div>
      </div>

      <div className="view-tabs">
        <button
          className={`view-tab ${view === 'graph' ? 'active' : ''}`}
          onClick={() => setView('graph')}
        >
          Graph
        </button>
        <button
          className={`view-tab ${view === 'list' ? 'active' : ''}`}
          onClick={() => setView('list')}
        >
          List
        </button>
      </div>

      {view === 'graph' ? (
        <div className="execution-graph-view">
          <div className="execution-graph-canvas">
            <PipelineCanvas
              pipeline={pipeline}
              onPipelineChange={() => {}}
              readonly
              nodeStatuses={nodeStatuses}
              onNodeSelect={setSelectedNodeId}
            />
          </div>
          {selectedStepName && (
            <>
              <div className="execution-graph-resizer" onMouseDown={handleDetailResizeStart} />
              <div className="execution-graph-detail" style={{ width: detailWidth }}>
                <NodeExecutionDetail
                  nodeId={selectedStepName}
                  nodeLabel={selectedNodeLabel}
                  execution={execution}
                  onClose={() => setSelectedNodeId(null)}
                />
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="node-executions">
          <h4>Node Execution Details</h4>
          <div className="node-list">
            {Object.entries(execution.node_executions).map(([nodeId, nodeExec]) => (
              <div key={nodeId} className={`node-execution-card status-${nodeExec.status}`}>
                <div className="node-execution-header">
                  <div className="node-header-left">
                    <div className={`node-status-indicator status-${nodeExec.status}`}>
                      {nodeExec.status === 'running' && (
                        <svg className="spinner" width="24" height="24" viewBox="0 0 24 24">
                          <circle className="spinner-circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" />
                        </svg>
                      )}
                      {nodeExec.status === 'completed' && <CheckIcon size={18} />}
                      {nodeExec.status === 'failed' && <CloseIcon size={18} />}
                      {nodeExec.status === 'pending' && <CircleIcon size={18} />}
                    </div>
                    <div className="node-title">
                      <h5>{nodeLabelsByStepName[nodeId] || nodeId}</h5>
                      {nodeLabelsByStepName[nodeId] && (
                        <span className="node-id-hint" title={`Step: ${nodeId}`}>
                          {nodeId.slice(0, 16)}...
                        </span>
                      )}
                      <span className={`status-badge status-badge-${nodeExec.status}`}>
                        {nodeExec.status.toLowerCase()}
                      </span>
                      {nodeExec.progress_message && nodeExec.status === 'running' && (
                        <span className="progress-message">{nodeExec.progress_message}</span>
                      )}
                    </div>
                  </div>
                  {nodeExec.started_at && nodeExec.completed_at && (
                    <div className="node-duration">
                      {((new Date(nodeExec.completed_at).getTime() - new Date(nodeExec.started_at).getTime()) / 1000).toFixed(2)}s
                    </div>
                  )}
                </div>

                <div className="node-execution-body">
                  {nodeExec.task_id && (
                    <div className="node-execution-meta">
                      <div className="meta-entry">
                        <span className="meta-label">Task ID:</span>
                        <code className="meta-value-full">{nodeExec.task_id}</code>
                        <button
                          className="copy-button-small"
                          onClick={() => copyToClipboard(nodeExec.task_id!)}
                          title="Copy task ID to clipboard"
                        >
                          <CopyIcon />
                        </button>
                      </div>
                    </div>
                  )}
                  {nodeExec.started_at && (
                    <div className="node-execution-time">
                      <div className="time-entry">
                        <span className="time-label">Started:</span>
                        <span className="time-value">{new Date(nodeExec.started_at).toLocaleString()}</span>
                      </div>
                      {nodeExec.completed_at && (
                        <div className="time-entry">
                          <span className="time-label">Completed:</span>
                          <span className="time-value">{new Date(nodeExec.completed_at).toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {nodeExec.error && (
                    <div className="node-execution-error">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        <path d="M8 4v5M8 11h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      <div>
                        <strong>Error:</strong>
                        <pre>{nodeExec.error}</pre>
                      </div>
                    </div>
                  )}

                  {nodeExec.instances[0]?.task_id && nodeExec.status === 'completed' && (
                    <NodeOutputSection executionId={execution.id} instance={nodeExec.instances[0]} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
